'use client';

import { motion } from 'framer-motion';
import Image from 'next/image';
import { sectionHeadingTag } from '@/components/dynamic-zone/section-heading-level';
import { components } from '@/types/strapi';
import { SectionContainer } from '../../ui/SectionContainer';

export default function FutureOfCommunications({
	heading,
	sub_heading,
	top_left,
	top_right,
	bottom_left,
	bottom_right,
	image,
	isFirst = false,
}: components['schemas']['DynamicZoneFutureOfCommunicationComponent'] & {
	isFirst?: boolean;
}) {
	const SectionHeading = sectionHeadingTag(isFirst);
	return (
		<SectionContainer className="future-of-communication-section relative mt-8 overflow-hidden">
			<noscript>
				<style>{`
					.future-of-communication-section h2[style],
					.future-of-communication-section h3[style],
					.future-of-communication-section p[style],
					.future-of-communication-section div[style*='opacity'],
					.future-of-communication-section div[style*='scale'] {
						opacity: 1 !important;
						transform: none !important;
					}
				`}</style>
			</noscript>
			{/* Background decoration */}
			<div className="absolute inset-0 -z-10">
				<div className="absolute left-1/4 top-0 h-72 w-72 rounded-full bg-gradient-to-br from-blue-100/20 to-purple-100/20 blur-3xl"></div>
				<div className="absolute bottom-0 right-1/4 h-72 w-72 rounded-full bg-gradient-to-br from-pink-100/20 to-orange-100/20 blur-3xl"></div>
			</div>

			<div className="mx-auto mb-10 max-w-3xl text-center">
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
					transition={{ duration: 0.6 }}>
					<SectionHeading className="mb-3 bg-gradient-to-r from-[#1E293B] to-primary bg-clip-text text-3xl font-bold text-transparent transition-all duration-500 hover:from-primary hover:to-[#1E293B] md:text-4xl lg:text-5xl">
						{heading}
					</SectionHeading>
					<div className="mx-auto mb-4 h-1 w-20 rounded-full bg-gradient-to-r from-primary to-purple-500 transition-transform duration-300 hover:scale-110"></div>
					{sub_heading && (
						<p className="text-2xl text-gray-600 transition-colors duration-300 hover:text-gray-800">
							{sub_heading}
						</p>
					)}
				</motion.div>
			</div>

			<div className="mx-auto max-w-7xl">
				<div className="mx-auto grid max-w-7xl items-center md:grid-cols-3">
					{/* Left Column */}
					<div className="space-y-8 md:space-y-12">
						{/* Top Left Box */}
						<motion.div
							className="relative"
							initial={{ opacity: 0, x: -20 }}
							whileInView={{ opacity: 1, x: 0 }}
							viewport={{ once: true }}
							transition={{ duration: 0.6 }}>
							<div className="group relative h-full">
								<div className="absolute -left-4 -top-4 transition-all duration-300 group-hover:rotate-180 group-hover:scale-150">
									<div className="h-8 w-8 rounded-full bg-gradient-to-br from-pink-400/20 to-pink-300/20 blur-sm"></div>
								</div>

								{top_left && (
									<p className="relative flex h-full flex-col rounded-xl border border-gray-100/20 bg-gradient-to-br from-white/80 to-white/50 p-6 text-lg leading-relaxed text-gray-700 shadow-[0_8px_30px_rgb(0,0,0,0.04)] backdrop-blur-sm transition-all duration-500 before:absolute before:inset-0 before:-z-10 before:rounded-xl before:bg-gradient-to-br before:from-pink-500/5 before:to-purple-500/5 before:opacity-0 before:transition-opacity before:duration-500 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(0,0,0,0.12)] group-hover:before:opacity-100">
										{top_left.title ? (
											<span className="text-xl font-bold">
												{top_left.title}
											</span>
										) : (
											<svg
												className="absolute left-6 top-6 h-4 w-4 text-pink-400 transition-colors duration-300 group-hover:text-pink-500"
												fill="currentColor"
												viewBox="0 0 24 24">
												<path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
											</svg>
										)}

										<span className="">
											{top_left.description}
										</span>
									</p>
								)}
							</div>
						</motion.div>

						{/* Bottom Left Box */}
						<motion.div
							className="relative"
							initial={{ opacity: 0, x: -20 }}
							whileInView={{ opacity: 1, x: 0 }}
							viewport={{ once: true }}
							transition={{ duration: 0.6, delay: 0.2 }}>
							<div className="group relative h-full">
								<div className="absolute -left-4 -top-4 transition-all duration-300 group-hover:rotate-180 group-hover:scale-150">
									<div className="h-8 w-8 rounded-full bg-gradient-to-br from-orange-400/20 to-orange-300/20 blur-sm"></div>
								</div>
								{bottom_left && (
									<p className="relative flex h-full flex-col rounded-xl border border-gray-100/20 bg-gradient-to-br from-white/80 to-white/50 p-6 text-lg leading-relaxed text-gray-700 shadow-[0_8px_30px_rgb(0,0,0,0.04)] backdrop-blur-sm transition-all duration-500 before:absolute before:inset-0 before:-z-10 before:rounded-xl before:bg-gradient-to-br before:from-pink-500/5 before:to-purple-500/5 before:opacity-0 before:transition-opacity before:duration-500 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(0,0,0,0.12)] group-hover:before:opacity-100">
										{bottom_left.title ? (
											<span className="text-xl font-bold">
												{bottom_left.title}
											</span>
										) : (
											<svg
												className="absolute left-6 top-6 h-4 w-4 text-pink-400 transition-colors duration-300 group-hover:text-pink-500"
												fill="currentColor"
												viewBox="0 0 24 24">
												<path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
											</svg>
										)}

										<span className="">
											{bottom_left.description}
										</span>
									</p>
								)}
							</div>
						</motion.div>
					</div>

					{/* Center Column with Image */}
					<div className="flex items-center justify-center py-8 md:py-0">
						<motion.div
							className="w-full max-w-xs px-4"
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true }}
							transition={{ duration: 0.6, delay: 0.1 }}>
							<div className="group relative">
								<div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-blue-500/10 to-indigo-500/10 blur-3xl transition-all duration-500 group-hover:from-blue-500/20 group-hover:to-indigo-500/20"></div>
								<div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-purple-500/10 to-pink-500/10 blur-3xl transition-all duration-500 group-hover:from-purple-500/20 group-hover:to-pink-500/20"></div>
								<motion.div
									initial={{ y: 0 }}
									animate={{
										y: [0, -10, 0],
										rotate: [0, 2, 0],
									}}
									transition={{
										duration: 5,
										repeat: Infinity,
										ease: 'easeInOut',
									}}
									className="relative z-10">
									{image && image.url && (
										<Image
											src={image.url}
											alt={image.alternativeText || ''}
											width={600}
											height={600}
											className="h-auto w-full transform transition-all duration-500 group-hover:drop-shadow-2xl"
										/>
									)}
								</motion.div>
							</div>
						</motion.div>
					</div>

					{/* Right Column */}
					<div className="space-y-8 md:space-y-12">
						{/* Top Right Box */}
						<motion.div
							className="relative"
							initial={{ opacity: 0, x: 20 }}
							whileInView={{ opacity: 1, x: 0 }}
							viewport={{ once: true }}
							transition={{ duration: 0.6 }}>
							<div className="group relative h-full">
								<div className="absolute -left-4 -top-4 transition-all duration-300 group-hover:rotate-180 group-hover:scale-150">
									<div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-400/20 to-blue-300/20 blur-sm"></div>
								</div>
								{top_right && (
									<p className="relative flex h-full flex-col rounded-xl border border-gray-100/20 bg-gradient-to-br from-white/80 to-white/50 p-6 text-lg leading-relaxed text-gray-700 shadow-[0_8px_30px_rgb(0,0,0,0.04)] backdrop-blur-sm transition-all duration-500 before:absolute before:inset-0 before:-z-10 before:rounded-xl before:bg-gradient-to-br before:from-pink-500/5 before:to-purple-500/5 before:opacity-0 before:transition-opacity before:duration-500 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(0,0,0,0.12)] group-hover:before:opacity-100">
										{top_right.title ? (
											<span className="text-xl font-bold">
												{top_right.title}
											</span>
										) : (
											<svg
												className="absolute left-6 top-6 h-4 w-4 text-pink-400 transition-colors duration-300 group-hover:text-pink-500"
												fill="currentColor"
												viewBox="0 0 24 24">
												<path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
											</svg>
										)}

										<span className="">
											{top_right.description}
										</span>
									</p>
								)}
							</div>
						</motion.div>

						{/* Bottom Right Box */}
						<motion.div
							className="relative"
							initial={{ opacity: 0, x: 20 }}
							whileInView={{ opacity: 1, x: 0 }}
							viewport={{ once: true }}
							transition={{ duration: 0.6, delay: 0.2 }}>
							<div className="group relative h-full">
								<div className="absolute -left-4 -top-4 transition-all duration-300 group-hover:rotate-180 group-hover:scale-150">
									<div className="h-8 w-8 rounded-full bg-gradient-to-br from-purple-400/20 to-purple-300/20 blur-sm"></div>
								</div>
								{bottom_right && (
									<p className="relative flex h-full flex-col rounded-xl border border-gray-100/20 bg-gradient-to-br from-white/80 to-white/50 p-6 text-lg leading-relaxed text-gray-700 shadow-[0_8px_30px_rgb(0,0,0,0.04)] backdrop-blur-sm transition-all duration-500 before:absolute before:inset-0 before:-z-10 before:rounded-xl before:bg-gradient-to-br before:from-pink-500/5 before:to-purple-500/5 before:opacity-0 before:transition-opacity before:duration-500 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(0,0,0,0.12)] group-hover:before:opacity-100">
										{bottom_right.title ? (
											<span className="text-xl font-bold">
												{bottom_right.title}
											</span>
										) : (
											<svg
												className="absolute left-6 top-6 h-4 w-4 text-pink-400 transition-colors duration-300 group-hover:text-pink-500"
												fill="currentColor"
												viewBox="0 0 24 24">
												<path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
											</svg>
										)}

										<span className="">
											{bottom_right.description}
										</span>
									</p>
								)}
							</div>
						</motion.div>
					</div>
				</div>
			</div>
		</SectionContainer>
	);
}
